import { supabase } from '../supabaseClient';
import type { Client, ClientStatus } from '@shared/types';

// Helper to transform database row to Client object
function transformClient(row: any): Client {
  return {
    id: row.id,
    name: row.name,
    shortName: row.short_name,
    entityType: row.entity_type,
    logoUrl: row.logo_url,
    addressLine1: row.address_line_1,
    addressLine2: row.address_line_2,
    city: row.city,
    state: row.state,
    zip: row.zip,
    country: row.country ?? 'US',
    website: row.website,
    industry: row.industry,
    notes: row.notes,
    status: row.status,
    externalSource: row.external_source,
    externalId: row.external_id,
    externalData: row.external_data,
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export const clientService = {
  async getClients(filters?: { status?: ClientStatus; search?: string }) {
    let query = supabase
      .from('clients')
      .select('*')
      .order('name', { ascending: true });

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }

    if (filters?.search) {
      query = query.or(
        `name.ilike.%${filters.search}%,city.ilike.%${filters.search}%,state.ilike.%${filters.search}%`
      );
    }

    const { data, error } = await query;
    
    if (error) throw error;
    return data ? data.map(transformClient) : [];
  },

  async getClient(id: string) {
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return transformClient(data);
  },

  async createClient(client: Partial<Client>) {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('clients')
      .insert([{
        name: client.name,
        short_name: client.shortName || null,
        entity_type: client.entityType || null,
        logo_url: client.logoUrl || null,
        address_line_1: client.addressLine1 || null,
        address_line_2: client.addressLine2 || null,
        city: client.city || null,
        state: client.state || null,
        zip: client.zip || null,
        country: client.country || 'US',
        website: client.website || null,
        industry: client.industry || null,
        notes: client.notes || null,
        status: client.status || 'active',
        external_source: client.externalSource || null,
        external_id: client.externalId || null,
        external_data: client.externalData || null,
        created_by: user.id,
      }])
      .select()
      .single();
    
    if (error) {
      console.error('Create client error:', error);
      throw error;
    }
    
    return transformClient(data);
  },

  async updateClient(id: string, updates: Partial<Client>) {
    const { data, error} = await supabase
      .from('clients')
      .update({
        name: updates.name,
        short_name: updates.shortName || null,
        entity_type: updates.entityType || null,
        logo_url: updates.logoUrl || null,
        address_line_1: updates.addressLine1 || null,
        address_line_2: updates.addressLine2 || null,
        city: updates.city || null,
        state: updates.state || null,
        zip: updates.zip || null,
        country: updates.country || 'US',
        website: updates.website || null,
        industry: updates.industry || null,
        notes: updates.notes || null,
        status: updates.status,
      })
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return transformClient(data);
  },

  async getClientByExternalId(externalSource: string, externalId: string) {
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('external_source', externalSource)
      .eq('external_id', externalId)
      .maybeSingle();
    
    if (error) throw error;
    return data ? transformClient(data) : null;
  },

  async deleteClient(id: string) {
    const { error } = await supabase
      .from('clients')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  async getClientContracts(clientId: string) {
    const { data, error } = await supabase
      .from('contracts')
      .select('*')
      .eq('client_id', clientId)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  async enrichClient(clientName: string, existingData?: Partial<Client>) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-enrich-client`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          clientName,
          existingData: {
            shortName: existingData?.shortName,
            entityType: existingData?.entityType,
            website: existingData?.website,
            industry: existingData?.industry,
            addressLine1: existingData?.addressLine1,
            city: existingData?.city,
            state: existingData?.state,
            zip: existingData?.zip,
            country: existingData?.country,
          },
        }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to enrich client data');
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'Failed to enrich client data');
    }

    return result.data;
  },
};

